import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    AlertCircle,
    Bug,
    ChevronRight,
    CircleHelp,
    CreditCard,
    Globe,
    LifeBuoy,
    Loader2,
    Plus,
    RefreshCw,
    Search,
    Send,
    Server,
    Ticket
} from "lucide-react";
import {
    createTicket,
    getSupportStats,
    getTickets
} from "../services/supportService";
import "./SupportTicketsPage.css";
import PageShell from "../components/layout/PageShell";
import { getCrmProjects } from "../services/crmService";
import { useSearchParams } from "react-router-dom";

const statusOptions = [
    { value: "", label: "All Status" },
    { value: "open", label: "Open" },
    { value: "in_progress", label: "In Progress" },
    { value: "waiting", label: "Waiting" },
    { value: "resolved", label: "Resolved" },
    { value: "closed", label: "Closed" }
];

const priorityOptions = [
    { value: "", label: "All Priority" },
    { value: "low", label: "Low" },
    { value: "medium", label: "Medium" },
    { value: "high", label: "High" },
    { value: "urgent", label: "Urgent" }
];

const categoryOptions = [
    { value: "general", label: "General", icon: CircleHelp },
    { value: "bug", label: "Bug", icon: Bug },
    { value: "billing", label: "Billing", icon: CreditCard },
    { value: "website", label: "Website", icon: Globe },
    { value: "domain", label: "Domain", icon: Server },
    { value: "deployment", label: "Deployment", icon: RefreshCw },
    { value: "feature_request", label: "Feature Request", icon: LifeBuoy },
    { value: "account", label: "Account", icon: Ticket }
];

function formatStatus(value) {
    return (value || "open").replaceAll("_", " ");
}

function formatDate(value) {
    if (!value) return "-";
    return new Date(value).toLocaleString();
}

export default function SupportTicketsPage() {
    const [stats, setStats] = useState(null);
    const [tickets, setTickets] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [showCreate, setShowCreate] = useState(false);
    const [error, setError] = useState("");

    const [projects, setProjects] = useState([]);
    const navigate = useNavigate();

    const isGuid = (value) =>
        typeof value === "string" &&
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(value);

    const getProjectId = (project) => project?.id || project?.projectId || "";

    const loadProjects = async () => {
        try {
            const data = await getCrmProjects();
            setProjects(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error(error);
            setProjects([]);
        }
    };

    const [searchParams, setSearchParams] = useSearchParams();

    const [filters, setFilters] = useState({
        status: "",
        priority: "",
        category: "",
        search: "",
        page: 1,
        pageSize: 25
    });

    const [form, setForm] = useState({
        subject: "",
        description: "",
        category: "general",
        priority: "medium",
        projectId: ""
    });

    const activeCategory = useMemo(
        () => categoryOptions.find((x) => x.value === form.category) || categoryOptions[0],
        [form.category]
    );

    const loadData = async () => {
        setLoading(true);
        setError("");

        try {
            const [statsData, ticketData] = await Promise.all([
                getSupportStats(),
                getTickets(filters)
            ]);

            setStats(statsData);
            setTickets(ticketData?.items || []);
            setTotalCount(ticketData?.totalCount || 0);
        } catch (err) {
            setError(
                err?.response?.data?.message ||
                err?.message ||
                "Unable to load support tickets."
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
        loadProjects();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.status, filters.priority, filters.category, filters.page]);

    useEffect(() => {
        if (searchParams.get("new") === "1") {
            setShowCreate(true);
            setSearchParams({}, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        setFilters((prev) => ({ ...prev, page: 1 }));
        loadData();
    };

    const handleCreateTicket = async (e) => {
        e.preventDefault();

        if (!form.subject.trim() || !form.description.trim()) {
            setError("Subject and description are required.");
            return;
        }

        setCreating(true);
        setError("");

        try {

            const cleanProjectId = isGuid(form.projectId)
                ? form.projectId
                : null;

            await createTicket({
                subject: form.subject.trim(),
                description: form.description.trim(),
                category: form.category,
                priority: form.priority,
                projectId: cleanProjectId
            });

            setForm({
                subject: "",
                description: "",
                category: "general",
                priority: "medium",
                projectId: ""
            });

            setShowCreate(false);
            await loadData();
        } catch (err) {
            setError(
                err?.response?.data?.message ||
                err?.message ||
                "Unable to create support ticket."
            );
        } finally {
            setCreating(false);
        }
    };

    return (
        <PageShell title="Support Tickets">
            <div className="support-page">
                <div className="support-hero">
                    <div>
                        <span className="support-kicker">Support Hub</span>
                        <h1>Support Tickets</h1>
                        <p>
                            Track website, billing, domain, deployment, and account support requests in one place.
                        </p>
                    </div>

                    <div className="support-hero-actions">
                        <button
                            type="button"
                            className="support-btn secondary"
                            onClick={loadData}
                            disabled={loading}
                        >
                            <RefreshCw size={16} />
                            Refresh
                        </button>

                        <button
                            type="button"
                            className="support-btn primary"
                            onClick={() => setShowCreate(true)}
                        >
                            <Plus size={16} />
                            New Ticket
                        </button>
                    </div>
                </div>

                {stats && (
                    <div className="support-stats-grid">
                        <div className="support-stat-card">
                            <span>Open</span>
                            <strong>{stats.open ?? stats.Open ?? 0}</strong>
                        </div>
                        <div className="support-stat-card">
                            <span>In Progress</span>
                            <strong>{stats.inProgress ?? stats.InProgress ?? 0}</strong>
                        </div>
                        <div className="support-stat-card">
                            <span>Waiting</span>
                            <strong>{stats.waiting ?? stats.Waiting ?? 0}</strong>
                        </div>
                        <div className="support-stat-card">
                            <span>Resolved</span>
                            <strong>{stats.resolved ?? stats.Resolved ?? 0}</strong>
                        </div>
                        <div className="support-stat-card">
                            <span>Total</span>
                            <strong>{stats.total ?? stats.Total ?? 0}</strong>
                        </div>
                    </div>
                )}

                <div className="support-panel">
                    <div className="support-filters">
                        <form className="support-search" onSubmit={handleSearchSubmit}>
                            <Search size={16} />
                            <input
                                value={filters.search}
                                onChange={(e) =>
                                    setFilters((prev) => ({ ...prev, search: e.target.value }))
                                }
                                placeholder="Search ticket number, subject, description..."
                            />
                        </form>

                        <select
                            value={filters.status}
                            onChange={(e) =>
                                setFilters((prev) => ({ ...prev, status: e.target.value, page: 1 }))
                            }
                        >
                            {statusOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>

                        <select
                            value={filters.priority}
                            onChange={(e) =>
                                setFilters((prev) => ({ ...prev, priority: e.target.value, page: 1 }))
                            }
                        >
                            {priorityOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>

                        <select
                            value={filters.category}
                            onChange={(e) =>
                                setFilters((prev) => ({ ...prev, category: e.target.value, page: 1 }))
                            }
                        >
                            <option value="">All Categories</option>
                            {categoryOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {error && (
                        <div className="support-alert">
                            <AlertCircle size={16} />
                            {error}
                        </div>
                    )}

                    {loading ? (
                        <div className="support-loading">
                            <Loader2 className="spin" size={22} />
                            Loading support tickets...
                        </div>
                    ) : tickets.length === 0 ? (
                        <div className="support-empty">
                            <LifeBuoy size={36} />
                            <h3>No support tickets yet</h3>
                            <p>Create your first support ticket when you need help with a website, domain, billing, or deployment issue.</p>
                        </div>
                    ) : (
                        <div className="support-ticket-list">
                            {tickets.map((ticket) => (
                                <div className="support-ticket-card" key={ticket.id}>
                                    <div className="ticket-main">
                                        <div className="ticket-number">{ticket.ticketNumber}</div>
                                        <h3>{ticket.subject}</h3>
                                        <div className="ticket-meta">
                                            <span className={`pill status ${ticket.status}`}>
                                                {formatStatus(ticket.status)}
                                            </span>
                                            <span className={`pill priority ${ticket.priority}`}>
                                                {ticket.priority}
                                            </span>
                                            <span className="pill category">{ticket.category}</span>
                                            <span>Updated {formatDate(ticket.updatedAtUtc)}</span>
                                        </div>
                                    </div>

                                    <button
                                        type="button"
                                        className="ticket-open-btn"
                                        title="Open ticket"
                                        onClick={() => {
                                            navigate(`/support/tickets/${ticket.id}`);
                                        }}
                                    >
                                        <ChevronRight size={18} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="support-pagination">
                        <span>
                            Showing {tickets.length} of {totalCount}
                        </span>
                        <div>
                            <button
                                type="button"
                                disabled={filters.page <= 1}
                                onClick={() =>
                                    setFilters((prev) => ({ ...prev, page: Math.max(1, prev.page - 1) }))
                                }
                            >
                                Previous
                            </button>
                            <span>Page {filters.page}</span>
                            <button
                                type="button"
                                disabled={tickets.length < filters.pageSize}
                                onClick={() =>
                                    setFilters((prev) => ({ ...prev, page: prev.page + 1 }))
                                }
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>

                {showCreate && (
                    <div className="support-modal-backdrop">
                        <div className="support-modal">
                            <div className="support-modal-header">
                                <div>
                                    <span className="support-kicker">New Support Ticket</span>
                                    <h2>Create Ticket</h2>
                                </div>
                                <button
                                    type="button"
                                    className="modal-close"
                                    onClick={() => setShowCreate(false)}
                                >
                                    ×
                                </button>
                            </div>

                            <form onSubmit={handleCreateTicket} className="support-create-form">
                                <label>
                                    Subject
                                    <input
                                        value={form.subject}
                                        onChange={(e) =>
                                            setForm((prev) => ({ ...prev, subject: e.target.value }))
                                        }
                                        placeholder="Example: My website is not updating"
                                    />
                                </label>

                                <label>
                                    Project
                                    <select
                                        value={form.projectId}
                                        onChange={(e) =>
                                            setForm((prev) => ({
                                                ...prev,
                                                projectId: e.target.value
                                            }))
                                        }
                                    >
                                        <option value="">No project / General support</option>

                                        {projects
                                            .filter((project) => isGuid(getProjectId(project)))
                                            .map((project) => {
                                                const id = getProjectId(project);

                                                return (
                                                    <option key={id} value={id}>
                                                        {project.businessName || project.name || project.projectName || id}
                                                    </option>
                                                );
                                            })}
                                    </select>
                                </label>

                                <div className="form-grid">
                                    <label>
                                        Category
                                        <select
                                            value={form.category}
                                            onChange={(e) =>
                                                setForm((prev) => ({ ...prev, category: e.target.value }))
                                            }
                                        >
                                            {categoryOptions.map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </label>

                                    <label>
                                        Priority
                                        <select
                                            value={form.priority}
                                            onChange={(e) =>
                                                setForm((prev) => ({ ...prev, priority: e.target.value }))
                                            }
                                        >
                                            <option value="low">Low</option>
                                            <option value="medium">Medium</option>
                                            <option value="high">High</option>
                                            <option value="urgent">Urgent</option>
                                        </select>
                                    </label>
                                </div>

                                <div className="category-preview">
                                    {React.createElement(activeCategory.icon, { size: 18 })}
                                    <span>{activeCategory.label}</span>
                                </div>

                                <label>
                                    Description
                                    <textarea
                                        rows={7}
                                        value={form.description}
                                        onChange={(e) =>
                                            setForm((prev) => ({ ...prev, description: e.target.value }))
                                        }
                                        placeholder="Describe the issue, what you expected, and any project/domain details."
                                    />
                                </label>

                                <div className="support-modal-actions">
                                    <button
                                        type="button"
                                        className="support-btn secondary"
                                        onClick={() => setShowCreate(false)}
                                    >
                                        Cancel
                                    </button>

                                    <button
                                        type="submit"
                                        className="support-btn primary"
                                        disabled={creating}
                                    >
                                        {creating ? <Loader2 className="spin" size={16} /> : <Send size={16} />}
                                        Create Ticket
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </PageShell>
    );
}